import { Panel } from "../Panel"
import type { Predictions, ItemPrediction, CascadeChain, PortPrediction } from "../dashboard-types"
import { Sparkline } from "./Sparkline"

function ConfidenceBar({ value }: { value: number }) {
  const pct = Math.round(value * 100)
  const color = pct >= 70 ? "bg-emerald-400" : pct >= 50 ? "bg-amber-400" : "bg-red-400"
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-12 h-1 rounded-full bg-[color:var(--surface-alt)] overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[8px] tabular-nums text-[color:var(--text-dim)]">{pct}%</span>
    </div>
  )
}

function TopMovers({ items }: { items: ItemPrediction[] }) {
  // Sort by absolute predicted change, take top 5
  const sorted = [...items].sort((a, b) => Math.abs(b.predicted_change_pct) - Math.abs(a.predicted_change_pct))
  const top5 = sorted.slice(0, 5)

  return (
    <div className="px-3 py-2">
      <div className="text-[9px] uppercase tracking-wider text-[color:var(--text-dim)] mb-2">
        Top Movers (7-Day Forecast)
      </div>
      <div className="space-y-2">
        {top5.map((p) => {
          const arrow = p.direction === "up" ? "\u2191" : p.direction === "down" ? "\u2193" : "\u2192"
          const sign = p.predicted_change_pct >= 0 ? "+" : ""
          const color = p.direction === "up"
            ? "text-red-400"
            : p.direction === "down"
              ? "text-emerald-400"
              : "text-[color:var(--text-muted)]"
          const topDriver = p.drivers[0]
          return (
            <div key={p.item_id} className="flex flex-col gap-0.5">
              <div className="flex items-center gap-2">
                <span className={`text-sm font-mono ${color}`}>{arrow}</span>
                <span className="text-[10px] text-[color:var(--text)] font-medium truncate flex-1">
                  {p.item_id}
                </span>
                <span className={`text-[10px] tabular-nums font-mono ${color}`}>
                  {sign}{p.predicted_change_pct.toFixed(1)}%
                </span>
                <ConfidenceBar value={p.confidence} />
              </div>
              {topDriver && (
                <div className="pl-5 text-[8px] text-[color:var(--text-dim)] truncate">
                  {topDriver.factor}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function CascadeAlert({ cascade }: { cascade: CascadeChain }) {
  return (
    <div className="px-3 py-2 border-t border-[color:var(--border)]">
      <div className="text-[9px] text-[color:var(--text)] font-semibold mb-1.5">
        {cascade.trigger}
      </div>
      <div className="flex items-stretch gap-0 overflow-x-auto">
        {cascade.chain.map((step, i) => (
          <div key={step.step} className="flex items-center shrink-0">
            {i > 0 && (
              <span className="text-[color:var(--text-dim)] text-[10px] mx-1 font-mono">{"\u2192"}</span>
            )}
            <div className="flex flex-col items-center px-1.5 py-1 rounded bg-[color:var(--surface-alt)]/40 border border-[color:var(--border)]/50 min-w-[70px]">
              <span className="text-[8px] text-[color:var(--text)] font-medium text-center leading-tight">
                {step.item}
              </span>
              <span className="text-[9px] tabular-nums font-mono text-amber-400 mt-0.5">
                {step.predicted_impact}
              </span>
              <span className="text-[7px] text-[color:var(--text-dim)] mt-0.5">
                +{step.lag_days}d lag
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function PortOutlook({ ports }: { ports: PortPrediction[] }) {
  // Sort: worsening first, then stable, then improving
  const order: Record<string, number> = { worsening: 0, stable: 1, improving: 2 }
  const sorted = [...ports].sort((a, b) => (order[a.direction] ?? 1) - (order[b.direction] ?? 1))

  return (
    <div className="px-3 py-2 border-t border-[color:var(--border)]">
      <div className="text-[9px] uppercase tracking-wider text-[color:var(--text-dim)] mb-2">
        Port Outlook (7-Day)
      </div>
      <div className="space-y-1.5">
        {sorted.map((p) => {
          const dirColor = p.direction === "worsening"
            ? "text-red-400"
            : p.direction === "improving"
              ? "text-emerald-400"
              : "text-[color:var(--text-muted)]"
          const dirGlyph = p.direction === "worsening" ? "\u2191" : p.direction === "improving" ? "\u2193" : "\u2192"
          const sparkStroke = p.direction === "worsening" ? "#ef4444" : p.direction === "improving" ? "#10b981" : "#8a8a8a"
          return (
            <div key={p.port_id} className="flex items-center gap-2">
              <span className="text-[10px] text-[color:var(--text)] font-mono w-24 truncate">
                {p.port_id.replace("port-", "").toUpperCase()}
              </span>
              <span className={`text-[9px] ${dirColor}`}>{dirGlyph}</span>
              <div className="w-16 shrink-0">
                <Sparkline values={p.congestion_7d} height={14} stroke={sparkStroke} />
              </div>
              <ConfidenceBar value={p.confidence} />
            </div>
          )
        })}
      </div>
    </div>
  )
}

interface Props {
  predictions: Predictions
}

export function PredictionsSummary({ predictions }: Props) {
  const totalPredictions = predictions.items.length + predictions.ports.length

  return (
    <Panel
      title="PREDICTIVE ANALYSIS"
      live
      count={totalPredictions}
      badge={
        <span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-400 uppercase tracking-wider">
          AI
        </span>
      }
      helpText="7-day predictive forecasts with confidence bands, cascade chain analysis, and port congestion outlook"
    >
      <TopMovers items={predictions.items} />
      {predictions.cascade.length > 0 && (
        <div>
          <div className="px-3 pt-2 pb-1 border-t border-[color:var(--border)]">
            <div className="text-[9px] uppercase tracking-wider text-[color:var(--text-dim)]">
              Cascade Alerts
            </div>
          </div>
          {predictions.cascade.map((c, i) => (
            <CascadeAlert key={i} cascade={c} />
          ))}
        </div>
      )}
      <PortOutlook ports={predictions.ports} />
    </Panel>
  )
}
